import React, { Suspense, useEffect, useState, useSyncExternalStore } from 'react';
const { createPortal }: { createPortal: (children: React.ReactNode, container: Element, key: string) => React.ReactNode } = require('react-dom');
import { Provider } from 'react-redux';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { previewSourceStyle } from './style';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { store } from '../../store';
import { AppearancePreviewContext } from '../../theme/AppearanceContext';
import AppearanceTypographyProvider from '../../theme/AppearanceTypographyProvider';
import { ElementStyleScope } from '../EditableElement';
import { ElementTypographyContext } from '../ElementTypography';
import { ExistingPreviewContext } from './PreviewContext';
import { previewScreens } from './screens.web';
import { previewProject } from './fixtures';
import loadIcons from '../../icons';
import type { PreviewBridge } from './ExistingPreview.web';

loadIcons();
const bridge: PreviewBridge = (window.frameElement as any).studioPreviewBridge;
const Stack = createStackNavigator();
const params = { projectId: 1, project: previewProject, conversationId: 1, id: 1 };

class PreviewErrorBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { bridge.reportError('This screen preview could not render.'); }
  render() { return this.state.failed ? null : this.props.children; }
}
export default function PreviewHost() {
  useSyncExternalStore(bridge.subscribe, bridge.version, bridge.version);
  useEffect(() => {
    // RN-web inserts CSS with CSSOM, which MutationObserver cannot observe.
    let previous = '';
    const copyStyles = () => {
      const css = [...document.styleSheets].flatMap(sheet => { try { return [...sheet.cssRules].map(rule => rule.cssText); } catch { return []; } }).join('\n');
      if (css !== previous) { previous = css; bridge.styles(css); }
    };
    copyStyles(); const timer = window.setInterval(copyStyles, 100);
    return () => window.clearInterval(timer);
  }, []);
  return <Provider store={store}><AppearancePreviewContext.Provider value={bridge.theme}><AppearanceTypographyProvider>
    <SafeAreaProvider initialMetrics={{ frame: { x: 0, y: 0, width: bridge.width, height: 1200 }, insets: { top: 0, bottom: 0, left: 0, right: 0 } }}>
      <PreviewScreen key={bridge.slug} />
    </SafeAreaProvider>
  </AppearanceTypographyProvider></AppearancePreviewContext.Provider></Provider>;
}

function PreviewScreen() {
  const [hasSections, setHasSections] = useState(false);
  const Screen = previewScreens[bridge.slug as keyof typeof previewScreens] as React.ComponentType<any> | undefined;
  if (!Screen) return null;
  const renderSection = (name: string, children: React.ReactNode) => <PortaledSection name={name} onMount={() => setHasSections(true)}>{children}</PortaledSection>;
  const firstSlot = bridge.slots.values().next().value;
  const screen = <div style={previewSourceStyle(bridge.width, hasSections)} data-studio-screen-source={bridge.slug}>
    <NavigationContainer><Stack.Navigator screenOptions={{ headerShown: false, animation: 'none' }}>
      <Stack.Screen name="Sample" component={Screen} initialParams={params} />
    </Stack.Navigator></NavigationContainer>
  </div>;
  return <ExistingPreviewContext.Provider value={renderSection}><PreviewErrorBoundary><Suspense fallback={null}>
    {firstSlot ? createPortal(screen, firstSlot, 'screen-source') : null}
  </Suspense></PreviewErrorBoundary></ExistingPreviewContext.Provider>;
}

function PortaledSection({ name, onMount, children }: { name: string; onMount: () => void; children: React.ReactNode }) {
  useEffect(onMount, []);
  const slot = bridge.slots.get(name);
  return slot ? createPortal(<ElementTypographyContext.Provider value={bridge.elementStyles.get(name) || {}}><ElementStyleScope value={bridge.styleScopes.get(name)?.styles || {}} scope={bridge.styleScopes.get(name)?.scope || name}>{children}</ElementStyleScope></ElementTypographyContext.Provider>, slot, name) : null;
}
